const STORAGE_KEY = 'onesight-motion-pref';

/**
 * Shows a toast asking the user whether to reduce animations.
 * Only shown once — the answer is persisted in localStorage.
 *
 * @param {(reduced: boolean) => void} onChoice  Called after the user decides.
 *   `true`  → keep reduced state (no reload needed, page already static)
 *   `false` → user wants full experience; page reloads with the pref saved
 */
export function initMotionToast(onChoice) {
    if (localStorage.getItem(STORAGE_KEY)) return; // already decided

    const toast = document.createElement('div');
    toast.className = 'motion-toast';
    toast.setAttribute('role', 'dialog');
    toast.setAttribute('aria-live', 'polite');
    toast.setAttribute('aria-label', 'Motion preference');
    toast.innerHTML = `
        <div class="motion-toast__body">
            <svg class="motion-toast__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10"/>
                <path d="M8 12a4 4 0 0 1 8 0"/>
                <line x1="12" y1="8" x2="12" y2="8.01"/>
            </svg>
            <p class="motion-toast__message">
                Your system has <strong>reduced motion</strong> on.
                Should we simplify this page's animations?
            </p>
        </div>
        <div class="motion-toast__actions">
            <button class="motion-toast__btn motion-toast__btn--keep" type="button">
                Keep full experience
            </button>
            <button class="motion-toast__btn motion-toast__btn--reduce" type="button">
                Yes, reduce effects
            </button>
        </div>
    `;

    document.body.appendChild(toast);

    // Defer so the initial state (opacity 0, translateY) is painted before the transition fires
    requestAnimationFrame(() => {
        requestAnimationFrame(() => toast.classList.add('motion-toast--visible'));
    });

    function dismiss(choice) {
        localStorage.setItem(STORAGE_KEY, choice);
        toast.classList.remove('motion-toast--visible');
        toast.addEventListener('transitionend', () => toast.remove(), { once: true });
    }

    toast.querySelector('.motion-toast__btn--keep').addEventListener('click', () => {
        dismiss('full');
        // Reload so the full animation pipeline initialises cleanly from the top
        window.location.reload();
    });

    toast.querySelector('.motion-toast__btn--reduce').addEventListener('click', () => {
        dismiss('reduced');
        onChoice(true);
    });
}

/**
 * Shows the end-of-page "Restore full experience" banner when the user has
 * explicitly saved 'reduced' in localStorage. Clicking it clears the key
 * and reloads the page so the full animation pipeline initialises cleanly.
 */
export function initRestoreMotionBanner() {
    if (localStorage.getItem(STORAGE_KEY) !== 'reduced') return;

    const banner = document.getElementById('restoreMotion');
    const btn    = document.getElementById('restoreMotionBtn');
    if (!banner || !btn) return;

    banner.hidden = false;
    banner.removeAttribute('aria-hidden');

    btn.addEventListener('click', () => {
        localStorage.removeItem(STORAGE_KEY);
        window.location.reload();
    });
}

/** Returns the resolved motion preference, honouring the saved override. */
export function getMotionPref() {
    if(!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false; // OS-level setting is "no preference", so start with full experience
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'full') return false;      // user explicitly wants animations
    if (saved === 'reduced') return true;    // user explicitly wants reduced
    return false; // OS-level preference is reduce, and user hasn't overridden it, so start with full experience
}
